import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  ShoppingBag, 
  History, 
  Settings2, 
  X, 
  Plus, 
  Trash2, 
  Edit2, 
  Search, 
  Minus, 
  BookOpen, 
  Armchair, 
  Cookie, 
  Sparkles, 
  Film, 
  Crown,
  Box,
  Gift,
  Coins,
  Gamepad2,
  Music,
  Palette,
  Trophy,
  Ticket,
  IceCream
} from 'lucide-react';
import { match } from 'pinyin-pro';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { normalizeLanguage } from '../i18n';
import { useClassStore } from '../store/useClassStore';
import { useToast } from '../hooks/useToast';

// Icon mapping
const ICON_MAP: Record<string, React.ReactNode> = {
  'book': <BookOpen className="w-8 h-8 text-indigo-600" />,
  'seat': <Armchair className="w-8 h-8 text-purple-600" />,
  'cookie': <Cookie className="w-8 h-8 text-amber-700" />,
  'sticker': <Sparkles className="w-8 h-8 text-pink-600" />,
  'movie': <Film className="w-8 h-8 text-blue-600" />,
  'crown': <Crown className="w-8 h-8 text-yellow-600" />,
  'game': <Gamepad2 className="w-8 h-8 text-emerald-600" />,
  'music': <Music className="w-8 h-8 text-rose-600" />,
  'art': <Palette className="w-8 h-8 text-cyan-600" />,
  'trophy': <Trophy className="w-8 h-8 text-amber-500" />,
  'ticket': <Ticket className="w-8 h-8 text-slate-600" />,
  'icecream': <IceCream className="w-8 h-8 text-pink-500" />,
  'default': <Gift className="w-8 h-8 text-gray-600" />
};

interface Good {
  id: number;
  name: string;
  description?: string;
  price: number; // Coins
  stock: number;
  imageUrl?: string; // We'll use this for icon key for now, or actual URL
}

interface Student {
  id: number;
  name: string;
  badges: number;
  badgesUsed?: number;
  coins?: number;
  availablePoints?: number;
}

interface ClassOption {
  id: number;
  name: string;
}

interface StoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

interface ExchangeRecord {
  id: number;
  student: Student;
  good: Good;
  cost: number;
  createdAt: string;
}

export default function StoreModal({ isOpen, onClose, onUpdate }: StoreModalProps) {
  const { currentClass } = useClassStore();
  const { showToast, ToastComponent } = useToast();
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<'list' | 'history' | 'manage'>('list');
  const [goods, setGoods] = useState<Good[]>([]);
  const [exchangeRecords, setExchangeRecords] = useState<ExchangeRecord[]>([]);
  const [isGoodsLoading, setIsGoodsLoading] = useState(false);
  const [isGoodsLoaded, setIsGoodsLoaded] = useState(false);
  const [goodsClassId, setGoodsClassId] = useState<number | null>(null);
  const goodsRequestSeq = useRef(0);
  const exchangeRecordsRequestSeq = useRef(0);

  // Exchange State
  const [isExchangeModalOpen, setIsExchangeModalOpen] = useState(false);
  const [selectedGood, setSelectedGood] = useState<Good | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState<number | ''>('');
  const [searchQuery, setSearchQuery] = useState('');

  // Manage State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingGood, setEditingGood] = useState<Partial<Good>>({});
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importClasses, setImportClasses] = useState<ClassOption[]>([]);
  const [selectedSourceClassId, setSelectedSourceClassId] = useState<number | ''>('');
  const [sourceGoods, setSourceGoods] = useState<Good[]>([]);
  const [selectedImportGoodIds, setSelectedImportGoodIds] = useState<Set<number>>(new Set());
  const [importSearchQuery, setImportSearchQuery] = useState('');
  const [importIncludeStock, setImportIncludeStock] = useState(true);
  const [importOverwrite, setImportOverwrite] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  // Confirmation Modal
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [deleteGoodConfirmation, setDeleteGoodConfirmation] = useState<Good | null>(null);
  const [isDeletingGood, setIsDeletingGood] = useState(false);

  const currentClassId = currentClass?.id;
  const activeLanguage = normalizeLanguage(i18n.resolvedLanguage || i18n.language);

  const fetchGoods = useCallback(async (classId?: number) => {
    const resolvedClassId = classId ?? currentClassId;
    if (!resolvedClassId) return;
    const requestSeq = ++goodsRequestSeq.current;
    setIsGoodsLoading(true);
    setIsGoodsLoaded(false);
    try {
      const response = await api.get('/goods', {
        params: { classId: resolvedClassId },
      });
      if (requestSeq !== goodsRequestSeq.current) return;
      setGoods(response.data);
      setGoodsClassId(resolvedClassId);
      setIsGoodsLoaded(true);
    } catch (error) {
      console.error('Failed to fetch goods', error);
      if (requestSeq !== goodsRequestSeq.current) return;
      setIsGoodsLoaded(false);
    } finally {
      if (requestSeq === goodsRequestSeq.current) {
        setIsGoodsLoading(false);
      }
    }
  }, [currentClassId]);

  const fetchExchangeRecords = useCallback(async (classId?: number) => {
    const resolvedClassId = classId ?? currentClassId;
    if (!resolvedClassId) return;
    const requestSeq = ++exchangeRecordsRequestSeq.current;
    try {
      const response = await api.get('/exchange-records', {
        params: { classId: resolvedClassId },
      });
      if (requestSeq !== exchangeRecordsRequestSeq.current) return;
      setExchangeRecords(response.data);
    } catch (error) {
      console.error('Failed to fetch exchange records', error);
    }
  }, [currentClassId]);

  useEffect(() => {
    if (!isOpen) return;
    if (!currentClassId) {
      setGoods([]);
      setExchangeRecords([]);
      setGoodsClassId(null);
      setIsGoodsLoaded(false);
      setIsGoodsLoading(false);
      return;
    }
    if (activeTab === 'history') {
      fetchExchangeRecords(currentClassId);
      return;
    }
    fetchGoods(currentClassId);
  }, [isOpen, activeTab, currentClassId, fetchExchangeRecords, fetchGoods]);

  useEffect(() => {
    if (!isOpen) return;
    setGoods([]);
    setExchangeRecords([]);
    setGoodsClassId(null);
    setIsGoodsLoaded(false);
    setIsGoodsLoading(false);
    setIsExchangeModalOpen(false);
    setSelectedGood(null);
    setStudents([]);
    setSelectedStudentId('');
    setSearchQuery('');
    setIsEditModalOpen(false);
    setEditingGood({});
    setIsImportModalOpen(false);
    setImportClasses([]);
    setSelectedSourceClassId('');
    setSourceGoods([]);
    setSelectedImportGoodIds(new Set());
    setImportSearchQuery('');
    setImportIncludeStock(true);
    setImportOverwrite(false);
    setIsConfirmModalOpen(false);
    setDeleteGoodConfirmation(null);
    setIsDeletingGood(false);
    setIsImporting(false);
  }, [currentClassId, isOpen]);

  const fetchStudents = async (classId: number) => {
    try {
      const response = await api.get(`/students?classId=${classId}`);
      setStudents(response.data);
    } catch (error) {
      console.error('Failed to fetch students', error);
    }
  };

  const handleRedeemClick = (good: Good) => {
    setSelectedGood(good);
    setIsExchangeModalOpen(true);
    setSearchQuery('');
    // Fetch students for current class immediately
    if (currentClass) {
      fetchStudents(currentClass.id);
    } else {
        // Fallback or empty?
        setStudents([]);
    }
    setSelectedStudentId('');
  };

  const handlePreConfirmRedeem = (studentId: number) => {
    if (!selectedGood) return;
    
    setSelectedStudentId(studentId);

    // Check stock
    if (selectedGood.stock <= 0) {
      showToast(t('storeModal.toast.outOfStock'), 'error');
      return;
    }

    // Check available points
    const student = students.find(s => s.id === studentId);
    if (student && (student.availablePoints || 0) < selectedGood.price) {
      // Should not happen if UI is correct, but safety check
      showToast(t('storeModal.toast.insufficientCoins'), 'error');
      return;
    }

    setIsConfirmModalOpen(true);
  };

  const handleExecuteRedeem = async () => {
    if (!selectedGood || !selectedStudentId) return;

    const redeemedStudent = students.find(s => s.id === selectedStudentId);
    const redeemedGoodName = selectedGood.name;

    try {
      // 1. Deduct stock and badges via backend transaction
      await api.post('/exchange-records', {
        studentId: selectedStudentId,
        goodId: selectedGood.id
      });

      setIsConfirmModalOpen(false);
      setIsExchangeModalOpen(false);
      fetchGoods();
      // Notify parent to refresh student data
      if (onUpdate) onUpdate();

      showToast(
        t('storeModal.toast.redeemSuccess', {
          name: redeemedStudent?.name ?? '',
          goodName: redeemedGoodName,
        }),
        'success',
      );
    } catch (error) {
      console.error('Redeem failed', error);
      showToast(t('storeModal.toast.redeemFailed'), 'error');
    }
  };

  const handleStockUpdate = async (good: Good, delta: number) => {
    const newStock = Math.max(0, good.stock + delta);
    try {
      await api.put(`/goods/${good.id}`, { stock: newStock });
      // Optimistic update
      setGoods(goods.map(g => g.id === good.id ? { ...g, stock: newStock } : g));
    } catch (error) {
      console.error('Failed to update stock', error);
      fetchGoods(); // Revert
    }
  };

  const handleDeleteGood = async (id: number) => {
    try {
      await api.delete(`/goods/${id}`);
      setGoods((prev) => prev.filter(g => g.id !== id));
      showToast(t('storeModal.toast.deleteSuccess'), 'success');
    } catch (error) {
      console.error('Failed to delete good', error);
      showToast(t('storeModal.toast.deleteFailed'), 'error');
      throw error;
    }
  };

  const handleConfirmDeleteGood = async () => {
    if (!deleteGoodConfirmation || isDeletingGood) return;
    setIsDeletingGood(true);
    try {
      await handleDeleteGood(deleteGoodConfirmation.id);
      setDeleteGoodConfirmation(null);
    } finally {
      setIsDeletingGood(false);
    }
  };

  const handleSaveGood = async () => {
    if (!editingGood.name?.trim()) {
      showToast(t('storeModal.toast.enterName'), 'error');
      return;
    }
    if (editingGood.price === undefined) {
      showToast(t('storeModal.toast.enterPrice'), 'error');
      return;
    }
    if (editingGood.stock === undefined) {
      showToast(t('storeModal.toast.enterStock'), 'error');
      return;
    }

    try {
      if (editingGood.id) {
        await api.put(`/goods/${editingGood.id}`, editingGood);
      } else {
        await api.post('/goods', { ...editingGood, classId: currentClass?.id ?? undefined });
      }
      setIsEditModalOpen(false);
      fetchGoods();
    } catch (error) {
      console.error('Failed to save good', error);
    }
  };

  const getIcon = (iconName?: string) => {
    if (!iconName) return ICON_MAP['default'];
    return ICON_MAP[iconName] || ICON_MAP['default'];
  };

  const openImportModal = async () => {
    if (!currentClass?.id) {
      showToast(t('storeModal.toast.selectClassFirst'), 'error');
      return;
    }
    setIsImportModalOpen(true);
    setSelectedSourceClassId('');
    setSourceGoods([]);
    setSelectedImportGoodIds(new Set());
    setImportSearchQuery('');
    setImportIncludeStock(true);
    setImportOverwrite(false);

    try {
      const response = await api.get('/goods/import/classes', {
        params: { classId: currentClass.id },
      });
      setImportClasses(response.data ?? []);
    } catch (error) {
      console.error('Failed to fetch import classes', error);
      showToast(t('storeModal.toast.fetchClassesFailed'), 'error');
    }
  };

  const fetchSourceGoods = async (classId: number) => {
    try {
      const response = await api.get('/goods', { params: { classId } });
      const nextGoods = response.data ?? [];
      setSourceGoods(nextGoods);
      // Don't auto-select all goods by default
      setSelectedImportGoodIds(new Set(nextGoods.map((g: Good) => g.id)));
    } catch (error) {
      console.error('Failed to fetch source goods', error);
      showToast(t('storeModal.toast.fetchSourceGoodsFailed'), 'error');
    }
  };

  const handleExecuteImport = async () => {
    if (!currentClass?.id) return;
    if (!selectedSourceClassId) {
      showToast(t('storeModal.toast.selectSourceClass'), 'error');
      return;
    }
    if (selectedImportGoodIds.size === 0) {
      showToast(t('storeModal.toast.selectAtLeastOneGood'), 'error');
      return;
    }
    if (isImporting) return;

    setIsImporting(true);
    try {
      const response = await api.post(
        '/goods/import',
        {
          sourceClassId: selectedSourceClassId,
          goodIds: Array.from(selectedImportGoodIds),
          overwrite: importOverwrite,
          includeStock: importIncludeStock,
        },
        { params: { classId: currentClass.id } },
      );
      const { created, updated, skipped } = response.data ?? {};
      showToast(t('storeModal.toast.importDone', { created: created ?? 0, updated: updated ?? 0, skipped: skipped ?? 0 }), 'success');
      setIsImportModalOpen(false);
      fetchGoods();
    } catch (error) {
      console.error('Import failed', error);
      showToast(t('storeModal.toast.importFailed'), 'error');
    } finally {
      setIsImporting(false);
    }
  };

  // Filter students based on search query
  const filteredStudents = students.filter(student => {
    const query = searchQuery.trim();
    if (!query) return true;
    return student.name.toLowerCase().includes(query.toLowerCase()) ||
           !!match(student.name, query);
  });

  if (!isOpen) return null;
  const resolvedCurrentClassId = currentClassId ?? null;
  const goodsReady = isGoodsLoaded && goodsClassId === resolvedCurrentClassId;
  const displayGoods = goodsReady ? goods : [];

  const filteredSourceGoods = sourceGoods.filter((g) => {
    const query = importSearchQuery.trim();
    if (!query) return true;
    return (
      g.name.toLowerCase().includes(query.toLowerCase()) ||
      !!match(g.name, query)
    );
  });

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 animate-in fade-in duration-200">
      {ToastComponent}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}></div>
      
      <div className="relative bg-[#f8f9fc] rounded-[2rem] shadow-2xl w-full max-w-6xl h-[85vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-300">
        
        {/* Header */}
        <div className="bg-white px-4 md:px-6 py-3 md:py-4 flex flex-col md:flex-row items-center justify-between shadow-sm z-10 gap-3 md:gap-0">
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="bg-indigo-600 p-2 md:p-2.5 rounded-xl shrink-0">
              {activeTab === 'manage' ? <Settings2 className="w-6 h-6 md:w-8 md:h-8 text-white" /> : <ShoppingBag className="w-6 h-6 md:w-8 md:h-8 text-white" />}
            </div>
            <div className="flex flex-col justify-center">
              <h2 className="text-lg md:text-xl font-black text-gray-800 tracking-tight leading-none mb-1">
                {activeTab === 'manage' ? t('storeModal.header.manageTitle') : t('storeModal.header.storeTitle')}
              </h2>
              <p className="text-gray-400 font-bold tracking-widest text-[10px] md:text-xs uppercase leading-none mb-0.5">
                {activeTab === 'manage' ? 'STORE MANAGER' : 'MAGIC STORE'}
              </p>
               {/* <p className="text-gray-400 font-bold text-[10px] md:text-xs text-left leading-none">
                 {t('storeModal.header.tips')}
               </p> */}
            </div>
            
            {/* Close button for mobile - moved here for better UX */}
            <button onClick={onClose} className="md:hidden ml-auto p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex items-center gap-3 w-full md:w-auto overflow-x-auto md:overflow-visible">
             {/* Tabs */}
            <div className="bg-gray-100 p-1 rounded-xl flex shrink-0">
              <button 
                onClick={() => setActiveTab('list')}
                className={`px-3 md:px-5 py-1.5 rounded-lg text-xs md:text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'list' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {t('storeModal.tabs.list')}
              </button>
              <button 
                onClick={() => setActiveTab('history')}
                className={`px-3 md:px-5 py-1.5 rounded-lg text-xs md:text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'history' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {t('storeModal.tabs.history')}
              </button>
              <button 
                onClick={() => setActiveTab('manage')}
                className={`px-3 md:px-5 py-1.5 rounded-lg text-xs md:text-sm font-bold transition-all flex items-center gap-1 md:gap-2 whitespace-nowrap ${activeTab === 'manage' ? 'bg-orange-100 text-orange-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <Settings2 className="w-3 h-3 md:w-4 md:h-4" />
                <span className="hidden sm:inline">{t('storeModal.tabs.manageLong')}</span>
                <span className="sm:hidden">{t('storeModal.tabs.manageShort')}</span>
              </button>
            </div>

            <button onClick={onClose} className="hidden md:block p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-8 custom-scrollbar">
          
          {/* List View */}
          {activeTab === 'list' && (
            isGoodsLoading || !goodsReady ? (
              <div className="h-full" />
            ) : displayGoods.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 py-20">
                <ShoppingBag className="w-16 h-16 mb-4 opacity-20" />
                <p className="font-bold text-lg mb-6">{t('storeModal.emptyList')}</p>
                <button 
                  onClick={() => {
                    setActiveTab('manage');
                    setEditingGood({});
                    setIsEditModalOpen(true);
                  }}
                  className="px-6 py-2.5 bg-indigo-500 hover:bg-indigo-600 text-white font-bold rounded-xl shadow-lg shadow-indigo-200 transition-all transform hover:-translate-y-0.5 flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  {t('storeModal.emptyListAction')}
                </button>
              </div>
            ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
              {displayGoods.map(good => (
                <div
                  key={good.id}
                  className="bg-white rounded-clay p-3 md:p-4 shadow-clay-sm hover:shadow-clay border border-transparent hover:border-pink-100 transition-all duration-200 ease-out group flex flex-col items-center relative overflow-hidden min-w-0 hover:-translate-y-0.5"
                >
                   {/* Background decor — soft pink halo */}
                   <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[140%] h-24 bg-gradient-to-b from-pink-50 to-transparent rounded-b-[100%] z-0"></div>

                   {/* Price Badge */}
                   <div className="absolute top-2 right-2 bg-gradient-to-br from-yellow-400 to-orange-400 text-white font-black text-[10px] px-1.5 py-0.5 rounded-full shadow-md shadow-yellow-200 z-20 flex items-center gap-0.5">
                    <Coins className="w-3 h-3 md:w-3.5 md:h-3.5 fill-current" />
                     {good.price}
                   </div>

                   {/* Icon */}
                   <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-2xl shadow-clay-sm flex items-center justify-center mb-3 z-10 relative">
                      {getIcon(good.imageUrl)}
                   </div>

                   <h3 className="text-sm md:text-base font-bold text-gray-800 mb-0.5 text-center relative z-10">{good.name}</h3>
                   <p className="text-[10px] md:text-xs text-gray-400 font-medium mb-3 md:mb-4 text-center line-clamp-2 min-h-[2.5em] relative z-10">
                     {good.description || t('storeModal.noDescription')}
                   </p>

                   <div className="w-full flex flex-col sm:flex-row items-stretch sm:items-center justify-between mt-auto gap-2 min-w-0 relative z-10">
                     <div className="flex items-center justify-center sm:justify-start gap-1 text-gray-400 font-bold text-xs bg-gray-50 px-2 py-0.5 rounded-lg shrink-0">
                       <Box className="w-3 h-3" />
                       {good.stock}
                     </div>
                     <button
                       onClick={() => handleRedeemClick(good)}
                       disabled={good.stock <= 0}
                       className="bg-gradient-to-r from-[#ff4da6] to-[#ff7a59] hover:brightness-105 disabled:from-gray-200 disabled:to-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-xs font-bold px-2 md:px-3 py-1.5 rounded-xl shadow-md shadow-pink-200/60 transition-all duration-200 ease-out hover:-translate-y-0.5 active:scale-[0.97] whitespace-nowrap w-full sm:flex-1 min-w-0"
                     >
                       {good.stock > 0 ? t('storeModal.redeemNow') : t('storeModal.soldOut')}
                     </button>
                   </div>
                </div>
              ))}
            </div>
          )
          )}

          {/* Manage View */}
          {activeTab === 'manage' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-bold text-gray-400">
                  {currentClass?.name ? t('storeModal.currentClass', { name: currentClass.name }) : t('storeModal.noClassSelected')}
                </div>
                {/* Import from other family button removed — single family mode */}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
                {goods.map(good => (
                  <div key={good.id} className="bg-white rounded-2xl p-3 md:p-4 shadow-sm border-2 border-dashed border-indigo-100 hover:border-indigo-300 transition-all group flex flex-col items-center relative min-w-0">
                   
                   {/* Price Badge */}
                   <div className="absolute top-2 right-2 bg-yellow-400 text-white font-black text-[10px] px-1.5 py-0.5 rounded-full shadow-lg shadow-yellow-200 z-20 flex items-center gap-0.5">
                    <Coins className="w-3 h-3 md:w-3.5 md:h-3.5 fill-current" />
                     {good.price}
                   </div>

                   {/* Icon */}
                   <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-3">
                      {getIcon(good.imageUrl)}
                   </div>

                   <h3 className="text-sm md:text-base font-bold text-gray-800 mb-0.5 text-center">{good.name}</h3>
                   <p className="text-[10px] md:text-xs text-gray-400 mb-3 md:mb-4">{good.description || t('storeModal.noDescription')}</p>

                   {/* Stock Control */}
                   <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-1 mb-3 w-full justify-between">
                     <span className="text-[10px] font-bold text-gray-400 ml-1">{t('storeModal.stock')}</span>
                     <div className="flex items-center">
                       <button 
                         onClick={() => handleStockUpdate(good, -1)}
                         className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-indigo-600 hover:bg-white rounded-md transition-colors"
                       >
                         <Minus className="w-3 h-3" />
                       </button>
                       <span className="text-xs font-bold text-gray-700 w-6 text-center">{good.stock}</span>
                       <button 
                         onClick={() => handleStockUpdate(good, 1)}
                         className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-indigo-600 hover:bg-white rounded-md transition-colors"
                       >
                         <Plus className="w-3 h-3" />
                       </button>
                     </div>
                   </div>

                   <div className="w-full flex gap-2">
                     <button 
                       onClick={() => {
                         setEditingGood(good);
                         setIsEditModalOpen(true);
                       }}
                       className="flex-1 py-1.5 text-xs font-bold text-gray-500 hover:text-indigo-600 bg-white border border-gray-200 hover:border-indigo-200 rounded-xl flex items-center justify-center gap-1 transition-colors"
                     >
                       <Edit2 className="w-3 h-3" /> {t('storeModal.edit')}
                     </button>
                     <button 
                      onClick={() => setDeleteGoodConfirmation(good)}
                       className="w-8 flex items-center justify-center text-gray-400 hover:text-red-500 bg-white border border-gray-200 hover:border-red-200 rounded-xl transition-colors"
                     >
                       <Trash2 className="w-3.5 h-3.5" />
                     </button>
                   </div>
                  </div>
                ))}

                <button 
                  onClick={() => {
                    setEditingGood({});
                    setIsEditModalOpen(true);
                  }}
                  className="bg-gray-50 rounded-[2rem] p-6 border-2 border-dashed border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all flex flex-col items-center justify-center min-h-[280px] group cursor-pointer"
                >
                  <div className="w-20 h-20 bg-white rounded-full shadow-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Plus className="w-8 h-8 text-gray-300 group-hover:text-indigo-500" />
                  </div>
                  <span className="text-gray-400 font-bold group-hover:text-indigo-500">{t('storeModal.addNew')}</span>
                </button>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
               {exchangeRecords.length === 0 ? (
                 <div className="flex flex-col items-center justify-center h-full text-gray-400 py-20">
                    <History className="w-16 h-16 mb-4 opacity-20" />
                    <p>{t('storeModal.noExchangeRecords')}</p>
                 </div>
               ) : (
                 exchangeRecords.map(record => (
                   <div key={record.id} className="bg-white rounded-2xl p-4 flex items-center justify-between shadow-sm">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-orange-500 font-bold text-lg">
                          {record.student?.name?.[0] || t('storeModal.record.defaultAvatar')}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-bold text-gray-800">{record.student?.name || t('storeModal.record.unknownStudent')}</span>
                            <span className="text-gray-400 font-medium text-sm">{t('storeModal.record.redeemed')}</span>
                            <span className="font-bold text-indigo-500">{record.good?.name || t('storeModal.record.unknownGood')}</span>
                          </div>
                          <div className="text-xs text-gray-400 font-medium">
                            {new Date(record.createdAt).toLocaleString(activeLanguage)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100">
                         <Coins className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                         <span className="font-bold text-gray-700">{record.cost}</span>
                      </div>
                   </div>
                 ))
               )}
            </div>
          )}
        </div>
      </div>

      {/* Exchange Selection Modal */}
      {isExchangeModalOpen && selectedGood && (
        <div className="absolute inset-0 z-[160] flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-[#f8f9fc] rounded-[2rem] w-full max-w-4xl shadow-2xl animate-in zoom-in-95 overflow-hidden flex flex-col max-h-[85vh]">
             {/* Modal Header */}
             <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 flex items-center justify-between shrink-0 text-white relative overflow-hidden">
                <div className="flex items-center gap-4 relative z-10">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center text-white shadow-inner">
                    <div className="scale-125">
                       {getIcon(selectedGood.imageUrl)}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-black tracking-tight mb-1">{t('storeModal.exchangeSelect.title')}</h3>
                    <div className="flex items-center gap-2 text-indigo-100 font-medium">
                      <span>{selectedGood.name}</span>
                      <span className="w-1 h-1 bg-white/50 rounded-full"></span>
                      <span className="flex items-center gap-1">
                        {t('storeModal.exchangeSelect.needCoins', { price: selectedGood.price })}
                      </span>
                    </div>
                  </div>
                </div>

                <button 
                  onClick={() => setIsExchangeModalOpen(false)}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors relative z-10"
                >
                  <X className="w-6 h-6 text-white" />
                </button>
                
                {/* Decorative circles */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/4 blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/30 rounded-full translate-y-1/2 -translate-x-1/4 blur-2xl"></div>
             </div>

             {/* Search Bar */}
             <div className="p-6 pb-2 shrink-0">
               <div className="relative">
                 <Search className="absolute left-5 top-4 w-5 h-5 text-gray-400" />
                 <input 
                   type="text" 
                  placeholder={t('storeModal.exchangeSelect.searchPlaceholder')}
                   value={searchQuery}
                   onChange={(e) => setSearchQuery(e.target.value)}
                   className="w-full bg-white border-2 border-indigo-50 hover:border-indigo-100 focus:border-indigo-500 rounded-2xl pl-14 pr-6 py-4 outline-none transition-all text-lg font-bold text-gray-700 placeholder-gray-300 shadow-sm cursor-text"
                 />
               </div>
             </div>
             
            {/* Student Grid */}
            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
               <div className="grid grid-cols-2 gap-4">
                  {filteredStudents.map((student) => {
                    const balance = student.availablePoints || 0;
                    const canAfford = balance >= selectedGood.price;
                    return (
                      <div
                        key={student.id}
                        className={`bg-white rounded-3xl p-6 border-2 transition-all relative group overflow-hidden ${
                          canAfford
                            ? 'border-transparent hover:border-indigo-500 shadow-sm hover:shadow-xl hover:shadow-indigo-100'
                            : 'border-transparent opacity-70 grayscale-[0.5]'
                        }`}
                      >
                         <div className="flex flex-col items-center text-center">
                           <h4 className="text-xl font-black text-gray-800 mb-3">{student.name}</h4>

                           <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold mb-4 ${
                             canAfford ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                           }`}>
                             <Coins className={`w-4 h-4 ${canAfford ? 'text-green-600 fill-green-600' : 'text-red-500 fill-red-500'}`} />
                             <span>{balance}</span>
                           </div>

                           <div className={`text-xs text-gray-400 font-bold tracking-wider uppercase transition-opacity ${canAfford ? 'group-hover:opacity-0' : ''}`}>
                             {canAfford ? (
                               <span>{t('storeModal.exchangeSelect.currentCoins', { coins: balance })}</span>
                             ) : (
                               <span className="text-red-400">{t('storeModal.exchangeSelect.insufficientCoins')}</span>
                             )}
                           </div>
                         </div>

                         {/* Hover Overlay Button (Only if can afford) */}
                         {canAfford && (
                           <div className="absolute inset-x-0 bottom-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-200 bg-white/90  flex justify-center pb-6">
                             <button 
                               onClick={() => handlePreConfirmRedeem(student.id)}
                               className="px-8 py-2.5 bg-[#6366f1] hover:bg-[#5558e6] text-white text-sm font-bold rounded-full shadow-lg shadow-indigo-200 transition-colors"
                             >
                               {t('storeModal.exchangeSelect.clickRedeem')}
                             </button>
                           </div>
                         )}
                      </div>
                    );
                  })}
                </div>
                
                {filteredStudents.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                    <p className="font-bold text-lg">{t('storeModal.exchangeSelect.noStudentsFound')}</p>
                  </div>
                )}
             </div>

             {/* Footer Action */}
             <div className="p-6 pt-2 bg-gray-50/50 border-t border-gray-100 shrink-0">
               <button 
                 onClick={() => setIsExchangeModalOpen(false)}
                 className="w-full py-4 bg-white hover:bg-gray-50 text-gray-400 hover:text-gray-600 font-bold rounded-2xl transition-colors"
               >
                 {t('common.cancel')}
               </button>
             </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {isConfirmModalOpen && selectedGood && (
        <div className="absolute inset-0 z-[170] flex items-center justify-center bg-black/40 backdrop-blur-[2px] p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl animate-in zoom-in-95 relative overflow-hidden">
            <button 
               onClick={() => setIsConfirmModalOpen(false)}
               className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-500">
                <div className="w-6 h-6 border-2 border-indigo-500 rounded-full flex items-center justify-center">
                  <div className="w-3 h-2 border-l-2 border-b-2 border-indigo-500 -rotate-45 mb-0.5"></div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-800">{t('storeModal.confirmRedeem.title')}</h3>
            </div>

            <p className="text-gray-600 font-medium mb-6 text-lg leading-relaxed">
              {t('storeModal.confirmRedeem.desc', {
                name: students.find(s => s.id === selectedStudentId)?.name || '',
                goodName: selectedGood.name,
              })}
            </p>

            <div className="flex items-center gap-2 text-amber-600 font-bold bg-amber-50 px-4 py-3 rounded-xl mb-8">
                <Coins className="w-5 h-5 text-amber-500 fill-amber-500" />
                <span>{t('storeModal.confirmRedeem.cost', { price: selectedGood.price })}</span>
              </div>

            <div className="flex gap-3">
              <button 
                onClick={() => setIsConfirmModalOpen(false)}
                className="flex-1 py-3.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 font-bold rounded-xl transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button 
                onClick={handleExecuteRedeem}
                className="flex-1 py-3.5 bg-indigo-500 hover:bg-indigo-600 text-white font-bold rounded-xl shadow-lg shadow-indigo-200 transition-all"
              >
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteGoodConfirmation && (
        <div
          className="absolute inset-0 z-[175] flex items-center justify-center bg-black/40 backdrop-blur-[2px] p-4"
          onClick={() => !isDeletingGood && setDeleteGoodConfirmation(null)}
        >
          <div
            className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl animate-in zoom-in-95 relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setDeleteGoodConfirmation(null)}
              disabled={isDeletingGood}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-full transition-colors disabled:opacity-50 disabled:hover:bg-transparent"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-red-100 text-red-500 flex items-center justify-center">
                <Trash2 className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">{t('storeModal.confirmDelete.title')}</h3>
            </div>

            <p className="text-gray-600 font-medium mb-8 text-lg leading-relaxed">
              {t('storeModal.confirmDelete.desc', { name: deleteGoodConfirmation.name })}
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setDeleteGoodConfirmation(null)}
                disabled={isDeletingGood}
                className="flex-1 py-3.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 font-bold rounded-xl transition-colors disabled:opacity-50 disabled:hover:bg-white"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmDeleteGood}
                disabled={isDeletingGood}
                className="flex-1 py-3.5 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl shadow-lg shadow-red-200 transition-all disabled:opacity-60 disabled:hover:bg-red-500"
              >
                {isDeletingGood ? t('storeModal.confirmDelete.deleting') : t('storeModal.confirmDelete.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit/Add Modal */}
      {isEditModalOpen && (
        <div className="absolute inset-0 z-[160] flex items-center justify-center bg-black/20 backdrop-blur-[1px] p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl animate-in zoom-in-95">
             <h3 className="text-2xl font-black text-gray-800 mb-6">
               {editingGood.id ? t('storeModal.editModal.editTitle') : t('storeModal.editModal.addTitle')}
             </h3>
             
             <div className="space-y-5 mb-8">
                <div>
                  <label className="block text-sm font-bold text-gray-500 mb-2">
                    {t('storeModal.editModal.name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editingGood.name || ''}
                    onChange={(e) => setEditingGood({ ...editingGood, name: e.target.value })}
                    className="w-full bg-gray-50 border-2 border-transparent focus:border-indigo-500 rounded-xl px-4 py-3 outline-none transition-all font-bold text-gray-700 placeholder-gray-300 cursor-text"
                    placeholder={t('storeModal.editModal.namePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-500 mb-2">{t('storeModal.editModal.description')}</label>
                  <input
                    type="text"
                    value={editingGood.description || ''}
                    onChange={(e) => setEditingGood({ ...editingGood, description: e.target.value })}
                    className="w-full bg-gray-50 border-2 border-transparent focus:border-indigo-500 rounded-xl px-4 py-3 outline-none transition-all font-medium text-gray-600 placeholder-gray-300 cursor-text"
                    placeholder={t('storeModal.editModal.descriptionPlaceholder')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-500 mb-2">
                      {t('storeModal.editModal.price')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      max="9999"
                      value={editingGood.price !== undefined ? editingGood.price : ''}
                      onChange={(e) => setEditingGood({ ...editingGood, price: e.target.value === '' ? undefined : Number(e.target.value) })}
                      className="w-full bg-gray-50 border-2 border-transparent focus:border-indigo-500 rounded-xl px-4 py-3 outline-none transition-all font-bold text-gray-700 cursor-text"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-500 mb-2">
                      {t('storeModal.editModal.initialStock')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      max="9999"
                      value={editingGood.stock !== undefined ? editingGood.stock : ''}
                      onChange={(e) => setEditingGood({ ...editingGood, stock: e.target.value === '' ? undefined : Number(e.target.value) })}
                      className="w-full bg-gray-50 border-2 border-transparent focus:border-indigo-500 rounded-xl px-4 py-3 outline-none transition-all font-bold text-gray-700 cursor-text"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-500 mb-2">{t('storeModal.editModal.iconType')}</label>
                  <div className="grid grid-cols-6 gap-2">
                    {Object.keys(ICON_MAP).filter(k => k !== 'default').map(key => (
                      <button
                        key={key}
                        onClick={() => setEditingGood({ ...editingGood, imageUrl: key })}
                        className={`aspect-square rounded-xl flex items-center justify-center border-2 transition-all ${editingGood.imageUrl === key ? 'border-indigo-500 bg-indigo-50' : 'border-gray-100 hover:border-indigo-200'}`}
                      >
                        <div className="scale-75 pointer-events-none">
                          {ICON_MAP[key]}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
             </div>

             <div className="flex gap-3">
               <button 
                 onClick={() => setIsEditModalOpen(false)}
                 className="flex-1 py-3.5 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold rounded-2xl transition-colors"
               >
                 {t('common.cancel')}
               </button>
               <button 
                 onClick={handleSaveGood}
                 className="flex-1 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl shadow-lg shadow-indigo-200 transition-all"
               >
                 {t('common.save')}
               </button>
             </div>
          </div>
        </div>
      )}

      {isImportModalOpen && (
        <div
          className="absolute inset-0 z-[165] flex items-center justify-center bg-black/30 backdrop-blur-sm p-4"
          onClick={() => !isImporting && setIsImportModalOpen(false)}
        >
          <div
            className="bg-white rounded-3xl w-full max-w-4xl shadow-2xl animate-in zoom-in-95 overflow-hidden flex flex-col max-h-[85vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-indigo-500 to-blue-600 p-6 flex items-center justify-between shrink-0 text-white relative overflow-hidden">
              <div className="flex items-center gap-3 relative z-10">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center">
                  <Box className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-black tracking-tight mb-1">{t('storeModal.importModal.title')}</h3>
                  <div className="text-indigo-100 font-medium text-sm">
                    {t('storeModal.importModal.subtitle')}
                  </div>
                </div>
              </div>

              <button
                onClick={() => setIsImportModalOpen(false)}
                disabled={isImporting}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors relative z-10 disabled:opacity-60"
              >
                <X className="w-6 h-6 text-white" />
              </button>

              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/4 blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/30 rounded-full translate-y-1/2 -translate-x-1/4 blur-2xl"></div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-500 mb-2">
                    {t('storeModal.importModal.sourceClass')} <span className="text-red-500">*</span>
                    <span className="ml-2 text-xs text-gray-400 font-bold">{t('storeModal.importModal.required')}</span>
                  </label>
                  <select
                    value={selectedSourceClassId}
                    onChange={(e) => {
                      const next = e.target.value ? Number(e.target.value) : '';
                      setSelectedSourceClassId(next);
                      setSourceGoods([]);
                      setSelectedImportGoodIds(new Set());
                      if (next) fetchSourceGoods(next);
                    }}
                    className="w-full bg-gray-50 border-2 border-transparent focus:border-indigo-500 rounded-xl px-4 py-3 outline-none transition-all font-bold text-gray-700 cursor-pointer"
                    disabled={isImporting}
                  >
                    <option value="">{t('storeModal.importModal.sourceClassPlaceholder')}</option>
                    {importClasses.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* <div className="flex flex-col justify-end gap-3">
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-600 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={importOverwrite}
                      onChange={(e) => setImportOverwrite(e.target.checked)}
                      disabled={isImporting}
                      className="w-4 h-4 accent-orange-500"
                    />
                    覆盖同名商品（价格/描述/图标）
                  </label>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-600 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={importIncludeStock}
                      onChange={(e) => setImportIncludeStock(e.target.checked)}
                      disabled={isImporting}
                      className="w-4 h-4 accent-orange-500"
                    />
                    同步库存
                  </label>
                </div> */}
              </div>

              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-5 top-4 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('storeModal.importModal.searchPlaceholder')}
                    value={importSearchQuery}
                    onChange={(e) => setImportSearchQuery(e.target.value)}
                    className="w-full bg-white border-2 border-indigo-50 hover:border-indigo-100 focus:border-indigo-500 rounded-2xl pl-14 pr-6 py-4 outline-none transition-all text-base font-bold text-gray-700 placeholder-gray-300 shadow-sm cursor-text"
                    disabled={isImporting || !selectedSourceClassId}
                  />
                </div>
                <button
                  onClick={() => {
                    const next = new Set(selectedImportGoodIds);
                    filteredSourceGoods.forEach((g) => next.add(g.id));
                    setSelectedImportGoodIds(next);
                  }}
                  disabled={isImporting || !selectedSourceClassId || filteredSourceGoods.length === 0}
                  className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-2xl transition-colors disabled:opacity-60"
                >
                  {t('storeModal.importModal.selectAll')}
                </button>
                <button
                  onClick={() => setSelectedImportGoodIds(new Set())}
                  disabled={isImporting || !selectedSourceClassId || sourceGoods.length === 0}
                  className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-2xl transition-colors disabled:opacity-60"
                >
                  {t('storeModal.importModal.clear')}
                </button>
              </div>

              <div className="text-sm font-bold text-gray-400">
                {t('storeModal.importModal.selectedCount', { selected: selectedImportGoodIds.size, total: sourceGoods.length })}
              </div>

              {!selectedSourceClassId ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <Box className="w-16 h-16 mb-4 opacity-20" />
                  <p className="font-bold text-lg">{t('storeModal.importModal.selectSourceClassFirst')}</p>
                </div>
              ) : filteredSourceGoods.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <Box className="w-16 h-16 mb-4 opacity-20" />
                  <p className="font-bold text-lg">{t('storeModal.importModal.noImportableGoods')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {filteredSourceGoods.map((g) => {
                    const selected = selectedImportGoodIds.has(g.id);
                    return (
                      <button
                        key={g.id}
                        onClick={() =>
                          setSelectedImportGoodIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(g.id)) next.delete(g.id);
                            else next.add(g.id);
                            return next;
                          })
                        }
                        disabled={isImporting}
                        className={`w-full text-left rounded-2xl p-4 border-2 transition-all flex items-center gap-4 ${
                          selected
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-100 bg-white hover:border-indigo-200'
                        } disabled:opacity-60`}
                      >
                        <div
                          className={`w-6 h-6 rounded-lg flex items-center justify-center font-black text-sm ${
                            selected ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-400'
                          }`}
                        >
                          ✓
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-3">
                            <div className="font-black text-gray-800 truncate">{g.name}</div>
                            <div className="shrink-0 text-sm font-black text-amber-600 flex items-center gap-1">
                              <Coins className="w-4 h-4 text-amber-500 fill-amber-500" />
                              {g.price}
                            </div>
                          </div>
                          <div className="text-xs font-bold text-gray-400 mt-1 truncate">
                            {g.description || t('storeModal.noDescription')}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="p-6 bg-gray-50/50 border-t border-gray-100 shrink-0 flex gap-3">
              <button
                onClick={() => setIsImportModalOpen(false)}
                disabled={isImporting}
                className="flex-1 py-3.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 font-bold rounded-2xl transition-colors disabled:opacity-60"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleExecuteImport}
                disabled={isImporting || !selectedSourceClassId || selectedImportGoodIds.size === 0}
                className="flex-1 py-3.5 bg-indigo-500 hover:bg-indigo-600 text-white font-bold rounded-2xl shadow-lg shadow-indigo-200 transition-all disabled:opacity-60 disabled:hover:bg-indigo-500"
              >
                {isImporting ? t('storeModal.importModal.importing') : t('storeModal.importModal.startImport')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
